import { ControlMessage } from '../controlMessage/ControlMessage';
import { KeyCodeControlMessage } from '../controlMessage/KeyCodeControlMessage';
import { KeyToCodeMap } from '../googDevice/KeyToCodeMap';
import KeyEvent from '../googDevice/android/KeyEvent';
import VideoSettings from '../VideoSettings';
import Size from '../Size';
import { StreamClientLike } from './StreamClientLike';

const TAG = '[ControlOnlyClient]';

// 与 WsRoomStreamClient 的 reference profile 保持一致：server 对初始码率/帧率有下限要求，
// 若填过低的参数 pusher 可能不启动 → 后续控制消息也被忽略。我们接受少量视频带宽损耗以换取控制链路可用。
const WS_ROOM_LIGHT_BITRATE = 5_000_000;
const WS_ROOM_LIGHT_MAX_FPS = 30;
const WS_ROOM_LIGHT_I_FRAME_INTERVAL = 5;
const WS_ROOM_LIGHT_WIDTH = 1024;
const WS_ROOM_LIGHT_HEIGHT = 1920;
const TYPE_CHANGE_STREAM_PARAMETERS = 101;

export type ControlOnlyMode = 'ws_scrcpy' | 'ws_room';

export type ControlOnlyOptions = {
    mode: ControlOnlyMode;
    udid: string;
    wsUrl: string;
    phoneId: number;
    deviceName?: string;
};

/**
 * 轻量控制通道：只维护一条 WebSocket 用于向云机发送 scrcpy / ws_room 控制消息。
 * 不创建 player，不挂载 DOM，不解码视频。适合群控从机——画面靠截图轮询。
 */
export class ControlOnlyClient implements StreamClientLike {
    public readonly mode: ControlOnlyMode;
    public readonly phoneId: number;
    public readonly is_master_cloud = false;
    // BroadcastService 用 (client.player as any)?.udid 识别客户端，这里提供最小 shim
    public readonly player: any;

    private ws?: WebSocket;
    private readonly udid: string;
    private readonly wsUrl: string;
    private deviceName: string;
    private readonly pendingMessages: ArrayBufferView[] = [];
    private isDestroyed = false;
    private reconnectAttempts = 0;
    private reconnectTimer?: number;

    public static create(options: ControlOnlyOptions): ControlOnlyClient {
        return new ControlOnlyClient(options);
    }

    private constructor(options: ControlOnlyOptions) {
        this.mode = options.mode;
        this.udid = options.udid;
        this.wsUrl = options.wsUrl;
        this.phoneId = options.phoneId;
        this.deviceName = options.deviceName || options.udid;
        this.player = { udid: this.udid, getName: () => 'control-only' };
        this.connect();
    }

    public sendMessage(message: ControlMessage): void {
        const buffer = message.toBuffer();
        const wsReady = !!this.ws && this.ws.readyState === WebSocket.OPEN;
        console.debug(
            `${TAG} recv→forward mode=${this.mode} udid=${this.udid} wsReady=${wsReady} msgType=${(message as any)?.type}`,
        );
        this.sendBinary(buffer);
    }

    public sendNewVideoSetting(_videoSettings: VideoSettings): void {
        // no-op: 从机不渲染视频
    }

    public setHandleKeyboardEvents(_enabled: boolean): void {
        // no-op: 从机不监听物理键盘；key 事件由 master 通过 BroadcastService 注入
    }

    public getDeviceName(): string {
        return this.deviceName;
    }

    public getMaxSize(): Size | undefined {
        return undefined;
    }

    public getPlayer(): any {
        return this.player;
    }

    public sendKeyEvent(code: string, action: 'down' | 'up', metaState: number): void {
        const keyCode = KeyToCodeMap.get(code);
        if (typeof keyCode !== 'number') return;
        const actionCode = action === 'down' ? KeyEvent.ACTION_DOWN : KeyEvent.ACTION_UP;
        const msg = new KeyCodeControlMessage(actionCode, keyCode, 0, metaState);
        this.sendMessage(msg);
    }

    public sendBackspaceKey(): void {
        if (this.mode === 'ws_room') {
            this.sendJson({ keyCode: 67, touchType: 'keystroke' });
        } else {
            this.sendKeyEvent('Backspace', 'down', 0);
            this.sendKeyEvent('Backspace', 'up', 0);
        }
    }

    public sendCharacter(char: string): void {
        if (!char) return;
        this.sendImeText(char);
    }

    public sendImeText(text: string): void {
        if (!text) return;
        if (this.mode === 'ws_room') {
            this.sendJson({ text, touchType: 'inputBox' });
        }
        // ws_scrcpy 模式下从机的英文文本由 /containers/batch_transfer_text 统一下发，此处不处理
    }

    public onDisconnected = (): void => {
        this.destroy();
    };

    public destroy(): void {
        this.isDestroyed = true;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
        if (this.ws) {
            try {
                this.ws.onclose = null;
                this.ws.onerror = null;
                this.ws.onmessage = null;
                this.ws.onopen = null;
                this.ws.close();
            } catch (_error) {
                // ignore
            }
            this.ws = undefined;
        }
        this.pendingMessages.length = 0;
    }

    private connect(): void {
        if (this.isDestroyed) return;
        try {
            this.ws = new WebSocket(this.wsUrl);
        } catch (error) {
            console.warn(`${TAG} create ws failed, retry later`, error);
            this.scheduleReconnect();
            return;
        }
        this.ws.binaryType = 'arraybuffer';
        this.ws.onopen = () => {
            this.reconnectAttempts = 0;
            if (this.mode === 'ws_room') {
                // 握手：告诉 pusher 我们在线，但用最小码率/帧率，尽量少拉视频
                this.sendWsRoomInitialSettings();
            }
            this.flushPending();
        };
        this.ws.onmessage = () => {
            // 主动丢弃视频 / 设备消息——从机只作为控制通道
        };
        this.ws.onclose = () => {
            this.ws = undefined;
            this.scheduleReconnect();
        };
        this.ws.onerror = (err) => {
            console.warn(`${TAG} socket error`, err);
        };
    }

    private scheduleReconnect(): void {
        if (this.isDestroyed || this.reconnectTimer) return;
        if (this.reconnectAttempts >= 5) return;
        this.reconnectAttempts += 1;
        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = undefined;
            this.connect();
        }, 1500);
    }

    private sendBinary(buffer: ArrayBufferView): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(buffer as any);
        } else {
            this.pendingMessages.push(buffer);
        }
    }

    private sendJson(payload: Record<string, unknown>): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(payload));
        }
    }

    private flushPending(): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        while (this.pendingMessages.length) {
            const msg = this.pendingMessages.shift();
            if (msg) {
                try {
                    this.ws.send(msg as any);
                } catch (error) {
                    console.warn(`${TAG} flush send failed`, error);
                }
            }
        }
    }

    private sendWsRoomInitialSettings(): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        const buffer = new ArrayBuffer(36);
        const view = new DataView(buffer);
        let offset = 0;
        view.setUint8(offset, TYPE_CHANGE_STREAM_PARAMETERS);
        offset += 1;
        view.setInt32(offset, WS_ROOM_LIGHT_BITRATE, false);
        offset += 4;
        view.setInt32(offset, WS_ROOM_LIGHT_MAX_FPS, false);
        offset += 4;
        view.setUint8(offset, WS_ROOM_LIGHT_I_FRAME_INTERVAL);
        offset += 1;
        view.setUint16(offset, WS_ROOM_LIGHT_WIDTH, false);
        offset += 2;
        view.setUint16(offset, WS_ROOM_LIGHT_HEIGHT, false);
        offset += 2;
        view.setUint16(offset, 0, false);
        offset += 2;
        view.setUint16(offset, 0, false);
        offset += 2;
        view.setUint16(offset, 0, false);
        offset += 2;
        view.setUint16(offset, 0, false);
        offset += 2;
        view.setUint8(offset, 0);
        offset += 1;
        view.setInt8(offset, 0);
        offset += 1;
        view.setInt32(offset, 0, false);
        offset += 4;
        view.setInt32(offset, 0, false);
        offset += 4;
        view.setInt32(offset, 0, false);
        this.ws.send(buffer);
    }
}
